import {ChevronRightIcon} from '@heroicons/react/24/solid';
import React from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {Link} from 'react-router-dom';

import LoadingSpinner from 'src/components/LoadingSpinner';
import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext';
import {useProjects} from 'src/hooks/useProject';
import {ApiOrganization} from 'src/lib/api/auth';
import {Project, fetchProject} from 'src/lib/api/project';
import {DashboardSection} from 'src/lib/api/series';
import {makeUuidPrefix} from 'src/lib/routeUtils';
import {
  getGroupedSites,
  hasAnnualFlow,
  hasRollingSeasonalFlow,
  hasSeasonalFlow,
  hasShortTermFlow,
} from 'src/lib/siteUtils';

type Props = {currentOrganization: ApiOrganization};

export default function ProjectListPage({currentOrganization}: Props) {
  const {projects, isLoaded} = useProjects();
  const {t} = useTranslations();
  const queryClient = useQueryClient();

  // Using useProjects instead of useProjectsWithSites for faster first render
  // Then following up with prefetching the sites for the projects so they
  // are ready (or close to ready) when the user expands the project to see the sites
  React.useEffect(() => {
    if (!projects) return;
    projects.forEach((p) => {
      queryClient.prefetchQuery(['project', p.id], () => fetchProject(p.id));
    });
  }, [projects, queryClient]);

  let content;
  if (!isLoaded) {
    content = <LoadingSpinner />;
  } else if (projects?.length === 0) {
    content = <div>{t('noProjectsMessage')}</div>;
  } else {
    content = projects?.map((p) => (
      <ProjectListItem key={p.id} project={p} currentOrganization={currentOrganization} />
    ));
  }
  return (
    <PageContainer>
      <PageTitle pageStack={[{title: t('projectsSites'), to: '/:currentOrgIdPrefix/projects'}]} />
      {content}
    </PageContainer>
  );
}

function ProjectListItem({
  project,
  currentOrganization,
}: {
  project: Project;
  currentOrganization: ApiOrganization;
}) {
  const {t} = useTranslations();
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const toggle = () => setExpanded((prev) => !prev);

  // If it hasn't been preloaded yet, fetch the project when the user expands
  // the project to see the sites
  const shouldFetch = expanded && (!project.sites || project.sites.length === 0);
  const {data: projectWithSites, isFetching: isFetchingSites} = useQuery(
    ['project', project.id],
    () => fetchProject(project.id),
    {enabled: shouldFetch, keepPreviousData: true}
  );

  // Only show sites that have a flow forecast series type
  const siteOptions = getGroupedSites(projectWithSites ?? project)?.filter(
    (s) => hasShortTermFlow(s) || hasRollingSeasonalFlow(s)
  );

  return (
    <div className="mb-3">
      <div className="flex items-center">
        <Link
          to={`/${makeUuidPrefix(currentOrganization.id)}/projects/${project.id}`}
          className="text-blue-600 hover:text-blue-500"
        >
          {project.name}
        </Link>
        {project.isHidden && (
          <span className="ml-2 inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
            {t('internal')}
          </span>
        )}
        <button
          type="button"
          className="ml-2 h-6 w-6 text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-expanded={expanded}
          aria-controls={`project-${project.id}-sites`}
          aria-label={expanded ? 'Hide sites' : 'Show sites'}
          onClick={toggle}
        >
          <ChevronRightIcon
            className={`h-4 w-4 transition-transform ${expanded ? 'transform rotate-90' : ''}`}
          />
        </button>
      </div>
      {expanded && (
        <div
          id={`project-${project.id}-sites`}
          className="mt-2 ml-6 pl-4 border-l border-gray-200 space-y-2"
        >
          {isFetchingSites && !(siteOptions && siteOptions.length) ? (
            <div className="py-1">
              <LoadingSpinner />
            </div>
          ) : siteOptions && siteOptions.length ? (
            siteOptions.map((s) => (
              <div key={s.id} className="flex items-center justify-between max-w-lg">
                <Link
                  to={`/${makeUuidPrefix(currentOrganization.id)}/projects/${project.id}?siteId=${encodeURIComponent(
                    s.id
                  )}`}
                  className="text-sm text-gray-700 hover:text-blue-600"
                >
                  {s.name}
                </Link>
                {/* Add a tag to indicate the forecast series types */}
                <div className="flex">
                  {hasAnnualFlow(s) && (
                    <SiteForecastTag
                      forecastType={t(DashboardSection.Annual)}
                      className="bg-sky-100 text-sky-800"
                    />
                  )}
                  {hasSeasonalFlow(s) && (
                    <SiteForecastTag
                      forecastType={t(DashboardSection.Seasonal)}
                      className="bg-purple-100 text-purple-800"
                    />
                  )}
                  {hasShortTermFlow(s) && (
                    <SiteForecastTag
                      forecastType={t(DashboardSection.ShortTerm)}
                      className="bg-green-100 text-green-800"
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500">No sites</div>
          )}
        </div>
      )}
    </div>
  );
}

const SiteForecastTag = ({forecastType, className}: {forecastType: string; className: string}) => {
  return (
    <span
      className={`ml-2 inline-flex rounded-md  px-2 py-1 text-xs ring-1 ring-inset ring-gray-500/10 ${className}`}
    >
      {forecastType}
    </span>
  );
};
